import { useCallback, useMemo, useState } from 'react'

import {
  PostTypeContext,
  type Media,
  type CreatePostTypeInput,
} from '@tribeplatform/gql-client/types'
import {
  useCreatePostType,
  useUpdatePostType,
} from '@tribeplatform/react-sdk/hooks'
import { noop } from '@tribeplatform/react-ui-kit/hooks'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useStaticIntl } from '../../i18n/hooks/useStaticIntl.js'
import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { useNavigate } from '../../Link/index.js'
import { useCMS } from '../hooks/useCMS.js'
import { type ReactionItem, ReactionTypes, ReactionErrors } from '../types.js'
import {
  backfillActiveState,
  convertToReactionItems,
} from '../utils/converters.js'
import { initialReactions } from '../utils/legacyReactions.js'
import {
  getAllowedReactions,
  getCustomReactions,
  getReactionType,
  getSingleChoiceReactions,
  validateReactions,
} from '../utils/reactions.js'
import { replyTypeOptionsFactory } from '../utils/selectOptions.js'
import { sanitizePostType } from '../utils/utils.js'

interface HandleSubmitArgs {
  name: CreatePostTypeInput['name']
  reactionType: ReactionTypes
  singleChoice: boolean
  validReplyTypeId: CreatePostTypeInput['validReplyTypesIds'][number]
}

export const useModelSettings = (slug: string, isNew: boolean) => {
  const { $t } = useI18n()
  const navigate = useNavigate()
  const { postTypes, replyTypes } = useCMS()
  const almostPostType = useMemo(
    () => postTypes.find(postType => postType.slug === slug),
    [slug, postTypes],
  )
  const postType = useMemo(() => {
    if (isNew) {
      return almostPostType
    }

    return sanitizePostType(almostPostType)
  }, [almostPostType, isNew])
  const replyTypeOptions = useStaticIntl(replyTypeOptionsFactory, replyTypes)
  const [reactionItems, setReactionItems] = useState<ReactionItem[]>(
    convertToReactionItems(
      postType?.customReactions?.length
        ? postType?.customReactions
        : initialReactions,
    ),
  )
  const [reactionError, setReactionError] = useState<string>(null)
  const [icon, setIcon] = useState<Media>(postType?.icon ?? null)
  const { mutateAsync: createPostType, isLoading: isCreating } =
    useCreatePostType({
      fields: {
        actions: 'basic',
        authMemberProps: 'basic',
        customReactions: 'basic',
        icon: 'all',
        mappings: 'basic',
        nativeFieldsTemplates: 'basic',
        postFields: 'basic',
        recommendationsSettings: 'basic',
        slate: 'basic',
        validReplyTypes: 'basic',
      },
    })
  const { mutateAsync: updatePostType, isLoading: isUpdating } =
    useUpdatePostType()
  const defaultValues = useMemo<HandleSubmitArgs>(() => {
    if (isNew || !postType) {
      return {
        name: '',
        slug: '',
        reactionType: ReactionTypes.all,
        singleChoice: true,
        validReplyTypeId: null,
      }
    }

    const {
      name,
      slug,
      validReplyTypes,
      allowedReactions,
      singleChoiceReactions,
    } = postType
    const reactionType = getReactionType(allowedReactions)
    const isSingleChoice =
      singleChoiceReactions === null || singleChoiceReactions?.length > 0

    return {
      name,
      slug,
      reactionType,
      singleChoice: isSingleChoice,
      validReplyTypeId: validReplyTypes?.length ? validReplyTypes[0].id : null,
    }
  }, [isNew, postType])

  const checkIfReactionNew = useCallback(
    (id: string) => {
      if (isNew) {
        return true
      }

      const reactionItem = reactionItems.find(
        reactionItem => id === reactionItem.id,
      )

      if (!reactionItem) {
        return true
      }

      const { key: selectedReactionKey } = reactionItem
      const originalCustomReaction = postType?.customReactions?.find(
        ({ key }) => key === selectedReactionKey,
      )

      return !originalCustomReaction
    },
    [isNew, postType?.customReactions, reactionItems],
  )

  const setReaction = useCallback(
    (id: string, newReaction: ReactionItem) => {
      const newReactionItems = reactionItems.map(reactionItem => {
        if (reactionItem.id === id) {
          return newReaction
        }

        return reactionItem
      })

      setReactionItems(newReactionItems)
    },
    [reactionItems],
  )

  const handleReactionErrors = useCallback(
    (reactionError: ReactionErrors) => {
      switch (reactionError) {
        case ReactionErrors.name:
          setReactionError(
            $t({
              defaultMessage:
                'You must provide a unique name for all the reactions',
              id: 'Admin.CMS.Reaction.Names.Required',
            }),
          )
          break
        case ReactionErrors.icon:
          setReactionError(
            $t({
              defaultMessage:
                'You must provide a unique icon for all the reactions',
              id: 'Admin.CMS.Reaction.Icon.Required',
            }),
          )
          break
        case ReactionErrors.key:
          setReactionError(
            $t({
              defaultMessage:
                'You must provide a unique key for all the reactions',
              id: 'Admin.CMS.Reaction.Key.Required',
            }),
          )
          break
        case ReactionErrors.none:
          setReactionError(null)
          break
        default: {
          // Code should never reach the default case
          const exhaustiveCheck: never = reactionError
          noop(exhaustiveCheck)
        }
      }
    },
    [$t],
  )

  const handleSubmit = useCallback(
    async ({
      name,
      reactionType,
      singleChoice,
      validReplyTypeId,
    }: HandleSubmitArgs) => {
      const validReplyTypesIds = validReplyTypeId ? [validReplyTypeId] : []
      const iconId = icon?.id
      const almostCustomReactions = getCustomReactions(
        reactionType,
        reactionItems,
      )
      const customReactions = almostCustomReactions.map(backfillActiveState)
      const allowedReactions = getAllowedReactions(reactionType, reactionItems)
      const singleChoiceReactions = getSingleChoiceReactions(
        reactionType,
        singleChoice,
        reactionItems,
      )
      const reactionErrors = validateReactions(
        customReactions,
        allowedReactions,
        singleChoiceReactions,
      )
      handleReactionErrors(reactionErrors)

      if (reactionErrors !== ReactionErrors.none) {
        return
      }

      if (isNew) {
        try {
          const postType = await createPostType({
            input: {
              name,
              pluralName: '',
              description: '',
              context: PostTypeContext.post,
              iconId,
              validReplyTypesIds,
              customReactions,
              allowedReactions,
              singleChoiceReactions,
            },
          })
          toast({
            title: $t({
              defaultMessage: 'CMS model successfully created',
              id: 'Admin.CMS.Model.Create.Success',
            }),
            status: 'success',
          })
          navigate(`/manage/content/cms/${postType?.slug}?#fields`)
        } catch (error) {
          toast({
            title: $t({
              defaultMessage: 'Could not create CMS model',
              id: 'Admin.CMS.Model.Create.Error',
            }),
            status: 'error',
          })
        }

        return
      }

      try {
        await updatePostType({
          id: postType.id,
          input: {
            name,
            iconId,
            validReplyTypesIds,
            customReactions,
            allowedReactions,
            singleChoiceReactions,
          },
        })
        toast({
          title: $t({
            defaultMessage: 'CMS model successfully updated',
            id: 'Admin.CMS.Model.Update.Success',
          }),
          status: 'success',
        })
      } catch (error) {
        toast({
          title: $t({
            defaultMessage: 'Could not update CMS model',
            id: 'Admin.CMS.Model.Update.Error',
          }),
          status: 'error',
        })
      }
    },
    [
      $t,
      createPostType,
      handleReactionErrors,
      icon?.id,
      isNew,
      navigate,
      postType?.id,
      reactionItems,
      updatePostType,
    ],
  )

  return {
    postType,
    replyTypes,
    defaultValues,
    replyTypeOptions,
    reactionItems,
    icon,
    isCreating,
    isUpdating,
    reactionError,
    checkIfReactionNew,
    setReaction,
    setReactionItems,
    handleSubmit,
    setIcon,
  }
}
